/** @jsxImportSource @emotion/react */

import { Globe, Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';

import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { SyncLoader } from 'react-spinners';
import { theme } from 'styles/theme/theme';
import useIsDev from 'shared/hooks/use-is-dev';
import { history } from 'store/store';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import UDButton from 'ud-ui/components/button';
import { useMainLayout } from 'ud-ui/layout/main/context';
import { resetPressedState, setActiveBoard, setActiveTemplate } from '../../store/templates/templates';
import ListContainer from '../components/elements/list-container';
import TemplateItem from '../components/elements/template-item';
import useCreateModal from '../hooks/modals/template/create';
import useSuccessCopyModal from '../hooks/modals/template/success-copy';
import { useLoadTemplates } from '../hooks/use-load-templates';
import useTemplates from '../hooks/use-templates';

function Templates() {
  const state = useLocation().state;
  const dispatch = useDispatch();
  const { templates, isLoading: isTemplatesLoading } = useTemplates();
  const { isLoaded: isOrganizerLoaded, info } = useOrganizer();
  const { modal: createModal, open: openCreateModal } = useCreateModal();
  const { modal: successCopyModal, open: openSuccessCopyModal } = useSuccessCopyModal();
  const isDev = useIsDev();
  const isFeatureAvailable = useMemo(() => {
    if (isDev) {
      return true;
    }
    if (!info) {
      return false;
    }
    return info.availableFeatures.OBS_INTEGRATION;
  }, [info, isDev]);
  const { setHeaderContent } = useMainLayout();

  useLoadTemplates();

  const breadcrumb = useMemo(
    () => (
      <UDBreadcrumbs
        className="my-10"
        breadcrumbs={[
          { label: 'Турниры', to: Pages.TOURNEYS.INDEX },
          { label: 'Шаблоны', to: Pages.TEMPLATE.INDEX },
        ]}
      />
    ),
    [],
  );

  const openCatalog = useCallback(() => {
    history.push(Pages.TEMPLATE.CATALOG.INDEX);
  }, []);

  useEffect(() => {
    if (state && state.isCoppedTemplate) {
      openSuccessCopyModal();
    }

    dispatch(setActiveTemplate({ id: undefined }));
    dispatch(setActiveBoard({ id: undefined }));
    dispatch(resetPressedState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    setHeaderContent(breadcrumb);
    return () => setHeaderContent(null);
  }, [breadcrumb, setHeaderContent]);

  return (
    <>
      <div className={'d-flex w-100 justify-content-end mt-4 pr-6'} style={{ gap: 24 }}>
        <UDButton
          variant={'dark'}
          className="d-flex align-items-center"
          onClick={openCatalog}
          disabled={!isOrganizerLoaded || !isFeatureAvailable}
        >
          <Globe size={16} className={'mr-2'} />
          Готовые шаблоны
        </UDButton>
        <UDButton
          variant={'dark'}
          className="d-flex align-items-center"
          onClick={openCreateModal}
          disabled={!isOrganizerLoaded || !isFeatureAvailable}
        >
          {/* <UDIcon name="plus" componentProps={{ className: 'mr-2' }} /> */}
          <Plus size={16} className="mr-2" color={theme.colors.AccentNaplesYellow} />
          Добавить шаблон
        </UDButton>
      </div>
      <ListContainer
        className={'mt-4 px-6'}
        headerProps={{ title: 'Шаблоны' }}
        contentProps={{ className: 'h-100', style: { height: '100%' } }}
      >
        {isTemplatesLoading ? (
          <div className={'flex-center h-100 container'}>
            <SyncLoader />
          </div>
        ) : (
          templates.map((template, index) => <TemplateItem key={template.id} template={template} index={index} />)
        )}
      </ListContainer>
      {createModal}
      {successCopyModal}
    </>
  );
}

export default Templates;
